/**
 * AppConfigProvider — bootstrap provider for country/client config.
 *
 * 1. Fetches GET /v1/public/country-by-origin (resolved by backend from Origin header).
 * 2. On success: validates countryId, writes to Zustand, then renders children.
 * 3. On loading: shows full-page SkeletonLoader (single loader for config + session to avoid blink).
 * 4. On error or invalid data: shows AppConfigError disclaimer (contact administrator + retry).
 * Must wrap the app so that getCountryId() / getClientId() are never called before config is ready.
 */

import React, { useEffect } from 'react';
import { useAppConfig } from '@/services/app-config.queries';
import { useSessionCheck } from '@/features/auth/api/queries';
import { useBoundStore } from '@/stores';
import SkeletonLoader from '@/shared/components/skeletons/SkeletonLoader';
import { AppConfigError } from '@/shared/components/AppConfigError';

function isValidCountryId(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

export const AppConfigProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const setAppConfig = useBoundStore.use.setAppConfig();
  const appConfigReady = useBoundStore.use.appConfigReady();

  const { data, isLoading, isError, isSuccess, refetch } = useAppConfig();
  const { isLoading: isSessionLoading } = useSessionCheck();

  useEffect(() => {
    if (!isSuccess || !data) return;

    const { country, client, phoneConfig } = data;
    if (!isValidCountryId(country.id)) return;

    setAppConfig({
      countryId: country.id,
      clientId: typeof client.id === 'number' ? client.id : 0,
      countryName: country.name ?? null,
      clientName: client.name ?? null,
      defaultPhoneCountry: phoneConfig.defaultCountry ?? null,
      allowedPhoneCountries: phoneConfig.allowedCountries ?? null,
    });
  }, [isSuccess, data, setAppConfig]);

  // Error first: if config API failed or returned invalid data, show error so we don't get stuck on loader
  if (isError || (isSuccess && data && !isValidCountryId(data.country.id))) {
    return <AppConfigError onRetry={() => refetch()} />;
  }

  // Single bootstrap loader: config loading, store not ready, or session still loading (avoids unmount/remount blink).
  if (isLoading || !appConfigReady || isSessionLoading) {
    return <SkeletonLoader />;
  }

  return <>{children}</>;
};
